import { PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './sidenav.scss?inline'

/**
 * `dap-ds-sidenav`
 * @summary Side navigation is a list of links that are used to navigate to different sections of a page.
 * @element dap-ds-sidenav
 * @title - Side navigation
 *
 * @event {{ href: string, event: Event }} dds-item-click - Event fired when the side navigation item is clicked.
 *
 * @slot - The content of the side navigation.
 *
 * @csspart base - The main side navigation container.
 * @csspart menu - The menu of the side navigation.
 */
@customElement('dap-ds-sidenav')
export default class DapDSSideNav extends DdsElement {
  /** The label of the side navigation, if aria label is not provided, it will be used as aria label */
  @property({ reflect: true }) label: string

  /** The active href of the side navigation, to explicitly set the active item, if not provided, it will be set based on the current location */
  @property() activeHref: string

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    const activeItem = this.querySelector(
      `[href="${this.activeHref ? this.activeHref : window.location.pathname}"]`,
    )

    if (activeItem) {
      this.removeActive()
      this.setActive(activeItem)
    }

    const activeGroup = activeItem?.closest('dap-ds-sidenav-group')
    if (activeGroup) {
      activeGroup.setAttribute('open', 'true')
    }
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value)

    if (name === 'activehref') {
      this.removeActive()

      const activeItem = this.querySelector(`[href="${value}"]`)
      this.setActive(activeItem)
    }
  }

  private removeActive(): void {
    this.querySelectorAll('dap-ds-sidenav-item, dap-ds-sidenav-group').forEach(
      item => {
        item.removeAttribute('active')
      },
    )
  }

  private setActive(activeItem?: Element | null): void {
    if (activeItem) {
      activeItem.setAttribute('active', 'true')

      const activeGroup = activeItem.closest('dap-ds-sidenav-group')
      if (activeGroup) {
        activeGroup.setAttribute('open', 'true')
      }
    }
  }

  render() {
    return html`
      <aside
        part="base"
        class="sidenav"
        aria-label=${this.ariaLabel ?? this.label}>
        <menu part="menu">
          <slot></slot>
        </menu>
      </aside>
    `
  }
}
